import type ExcelJS from "exceljs";
import type { CashFlowRow, DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import { exportStyledPdf } from "@/components/finance/reports/shared/exportStyledPdf";
import type {
  ExportRow,
  RowKind,
} from "@/components/finance/reports/shared/exportStyledExcel";

type PeriodLabel = { key: string; label: string };

const FR_TITLE = "Cash Flow";

function withUnmappedSection(
  reportRows: CashFlowRow[],
  unmappedRows: CashFlowRow[]
): CashFlowRow[] {
  return [
    ...reportRows,
    ...(unmappedRows.length
      ? ([
          {
            key: "fr-unmapped-header",
            label: "UNMAPPED OPERATING POSITIONS",
            isHeader: true,
            values: new Map<string, number>(),
          } as CashFlowRow,
          ...unmappedRows,
        ] as CashFlowRow[])
      : []),
  ];
}

function frKind(r: CashFlowRow): RowKind {
  if (r.isHeader) return "header";
  if (r.key === "fr-net-operating" || r.key === "fr-closing") return "grandTotal";
  if (r.isTotal) return "total";
  return "data";
}

/** Build the shared ExportRow model for the financial report (values only). */
export function buildFinancialReportExportRows(
  reportRows: CashFlowRow[],
  unmappedRows: CashFlowRow[],
  periodLabels: PeriodLabel[]
): ExportRow[] {
  return withUnmappedSection(reportRows, unmappedRows).map((r) => {
    const kind = frKind(r);
    return {
      label: r.label,
      indent: r.indent ?? 0,
      kind,
      cells:
        kind === "header"
          ? undefined
          : periodLabels.map((p) => ({ value: r.values.get(p.key) ?? 0 })),
    };
  });
}

export const FINANCIAL_REPORT_TITLE = FR_TITLE;

export function exportFinancialReportToPdf(
  reportRows: CashFlowRow[],
  unmappedRows: CashFlowRow[],
  periodLabels: PeriodLabel[],
  displayCurrency: DisplayCurrency,
  fileName: string
) {
  return exportStyledPdf({
    title: FR_TITLE,
    periodLabels,
    rows: buildFinancialReportExportRows(reportRows, unmappedRows, periodLabels),
    displayCurrency,
    fileName,
  });
}

// Currency number formats (positive; (negative); zero-as-dash) — colors are
// applied via cell fonts instead of format color codes so the UI palette wins.
const CURRENCY_FORMATS: Record<DisplayCurrency, string> = {
  ETB: '"ETB" #,##0.00;("ETB" #,##0.00);"–"',
  USD: '"$"#,##0.00;("$"#,##0.00);"–"',
};

// Palette mirrored from the on-screen table (Tailwind slate/emerald/rose).
// exceljs colors are ARGB — prefix each hex with FF (fully opaque).
const C = {
  slate900: "FF0F172A",
  slate700: "FF334155",
  slate600: "FF475569",
  slate500: "FF64748B",
  slate300: "FFCBD5E1",
  slate200: "FFE2E8F0",
  slate100: "FFF1F5F9",
  slate50: "FFF8FAFC",
  white: "FFFFFFFF",
  emerald600: "FF059669",
  rose500: "FFF43F5E",
};

const FONT = "Calibri";

function solid(argb: string): ExcelJS.Fill {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function colLetter(index: number): string {
  // 0-based → A, B, ...
  let n = index;
  let s = "";
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

export async function exportFinancialReportToExcel(
  reportRows: CashFlowRow[],
  unmappedRows: CashFlowRow[],
  periodLabels: PeriodLabel[],
  displayCurrency: DisplayCurrency,
  fileName: string
) {
  const rows = [
    ...reportRows,
    ...(unmappedRows.length
      ? ([
          {
            key: "fr-unmapped-header",
            label: "UNMAPPED OPERATING POSITIONS",
            isHeader: true,
            values: new Map<string, number>(),
          } as CashFlowRow,
          ...unmappedRows,
        ] as CashFlowRow[])
      : []),
  ];

  const numFmt = CURRENCY_FORMATS[displayCurrency] ?? CURRENCY_FORMATS.ETB;
  const dataLastCol = periodLabels.length + 1; // 1 = label column, 2..N+1 = periods

  // Lazy-load exceljs so it is code-split out of the main bundle.
  const ExcelJSLib = (await import("exceljs")).default;
  const wb = new ExcelJSLib.Workbook();
  const ws = wb.addWorksheet("Cash Flow", {
    // Hide gridlines so the whole sheet is a clean surface, and freeze the
    // title + header rows and the label column.
    views: [{ state: "frozen", xSplit: 1, ySplit: 2, showGridLines: false }],
  });

  // Card frame: left border on the first column, right border on the last,
  // plus each row's own bottom rule. Draws the report as a bounded "card".
  const applyBorder = (
    cell: ExcelJS.Cell,
    colIdx: number,
    isGrandTotal: boolean
  ) => {
    const border: Partial<ExcelJS.Borders> = {
      bottom: {
        style: isGrandTotal ? "medium" : "thin",
        color: { argb: isGrandTotal ? C.slate700 : C.slate100 },
      },
    };
    if (colIdx === 1) border.left = { style: "medium", color: { argb: C.slate200 } };
    if (colIdx === dataLastCol)
      border.right = { style: "medium", color: { argb: C.slate200 } };
    cell.border = border;
  };

  // ── Row 1: dark title band ──────────────────────────────────────────────
  // Merge the title across everything EXCEPT the last column so the currency
  // can sit right-aligned there. (In a merged range every cell routes to the
  // master, so a merge over the whole row would clobber the title.) The whole
  // row shares the same dark fill, so it still reads as one continuous band.
  const titleMergeEnd = dataLastCol - 1;
  if (titleMergeEnd > 1) ws.mergeCells(1, 1, 1, titleMergeEnd);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = "Cash Flow";
  titleCell.font = { name: FONT, size: 14, bold: true, color: { argb: C.white } };
  titleCell.fill = solid(C.slate900);
  titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
  titleCell.border = { left: { style: "medium", color: { argb: C.slate200 } } };
  // Fill any middle band cells that aren't the master (so the dark band is
  // unbroken even if the merge doesn't cover them).
  for (let c = 2; c < dataLastCol; c++) ws.getCell(1, c).fill = solid(C.slate900);
  // Right-aligned currency meta in the last column.
  const titleMetaCell = ws.getCell(1, dataLastCol);
  titleMetaCell.value = `Currency: ${displayCurrency}`;
  titleMetaCell.font = { name: FONT, size: 10, color: { argb: C.slate300 } };
  titleMetaCell.fill = solid(C.slate900);
  titleMetaCell.alignment = { horizontal: "right", vertical: "middle", indent: 1 };
  titleMetaCell.border = { right: { style: "medium", color: { argb: C.slate200 } } };

  // ── Row 2: column header (Line Item + periods) ──────────────────────────
  const headerCell = (col: number, text: string, align: "left" | "right") => {
    const cell = ws.getCell(2, col);
    cell.value = text;
    cell.font = { name: FONT, size: 9, bold: true, color: { argb: C.slate500 } };
    cell.fill = solid(C.slate50);
    cell.alignment = { horizontal: align, vertical: "middle", indent: 1 };
    const border: Partial<ExcelJS.Borders> = {
      top: { style: "thin", color: { argb: C.slate200 } },
      bottom: { style: "medium", color: { argb: C.slate200 } },
    };
    if (col === 1) border.left = { style: "medium", color: { argb: C.slate200 } };
    if (col === dataLastCol)
      border.right = { style: "medium", color: { argb: C.slate200 } };
    cell.border = border;
  };
  headerCell(1, "LINE ITEM", "left");
  periodLabels.forEach((p, i) => headerCell(i + 2, p.label.toUpperCase(), "right"));

  // Track excel row (1-based) per report row for formula references.
  const rowMeta: { excelRow: number; row: CashFlowRow }[] = [];
  let excelRow = 3;

  for (const r of rows) {
    rowMeta.push({ excelRow, row: r });

    const isGrandTotal = r.key === "fr-net-operating" || r.key === "fr-closing";
    const isTotal = !!r.isTotal || isGrandTotal;
    const indent = r.indent ?? 0;

    // Row-level fill + label styling by category.
    let rowFill: string;
    let labelFontColor: string;
    let labelBold: boolean;
    if (isGrandTotal) {
      rowFill = C.slate900;
      labelFontColor = C.white;
      labelBold = true;
    } else if (r.isHeader || isTotal) {
      rowFill = C.slate50;
      labelFontColor = C.slate900;
      labelBold = true;
    } else {
      rowFill = C.white;
      labelFontColor = C.slate600;
      labelBold = false;
    }

    // Label cell
    const labelCell = ws.getCell(excelRow, 1);
    labelCell.value = r.label;
    labelCell.font = {
      name: FONT,
      size: r.isHeader ? 9 : 10,
      bold: labelBold,
      color: { argb: labelFontColor },
    };
    labelCell.fill = solid(rowFill);
    labelCell.alignment = {
      horizontal: "left",
      vertical: "middle",
      indent: 1 + indent,
    };
    applyBorder(labelCell, 1, isGrandTotal);

    if (r.isHeader) {
      // Extend the header fill across the period columns for a full band.
      for (let c = 0; c < periodLabels.length; c++) {
        const cell = ws.getCell(excelRow, c + 2);
        cell.fill = solid(rowFill);
        applyBorder(cell, c + 2, isGrandTotal);
      }
      excelRow += 1;
      continue;
    }

    // Period cells
    for (let c = 0; c < periodLabels.length; c++) {
      const pKey = periodLabels[c].key;
      const val = r.values.get(pKey) ?? 0;
      const cell = ws.getCell(excelRow, c + 2);

      // Only build SUM/derived formulas for known aggregate rows. Passthrough
      // rows (Cash Opening / Cash Closing) carry pre-computed converted values
      // and must be literals so the export matches the on-screen figures.
      const isFormulaRow =
        r.isTotal &&
        (r.key === "fr-total-outflow" ||
          r.key === "fr-net-operating" ||
          r.key === "fr-total-sales" ||
          r.key.startsWith("fr-sub-"));
      const totalFormula = isFormulaRow
        ? buildTotalFormula(r, rowMeta, c + 2)
        : null;

      cell.value = totalFormula ? { formula: totalFormula } : val;
      cell.numFmt = numFmt;

      // Value font color: totals/grand-totals use a fixed color; data rows
      // colour by sign like the UI (green positive, red negative, grey zero).
      let valColor: string;
      if (isGrandTotal) valColor = C.white;
      else if (isTotal) valColor = C.slate900;
      else if (val > 0) valColor = C.emerald600;
      else if (val < 0) valColor = C.rose500;
      else valColor = C.slate300;

      cell.font = { name: FONT, size: 10, bold: isTotal, color: { argb: valColor } };
      cell.fill = solid(rowFill);
      cell.alignment = { horizontal: "right", vertical: "middle", indent: 1 };
      applyBorder(cell, c + 2, isGrandTotal);
    }

    excelRow += 1;
  }

  // Column widths + row heights.
  ws.getColumn(1).width = 46;
  for (let c = 2; c <= dataLastCol; c++) ws.getColumn(c).width = 16;
  ws.getRow(1).height = 30;
  ws.getRow(2).height = 22;

  // ── Download (mirror the repo's Blob/anchor pattern) ────────────────────
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildTotalFormula(
  row: CashFlowRow,
  rowMeta: { excelRow: number; row: CashFlowRow }[],
  colIndex: number
): string | null {
  const col = colLetter(colIndex - 1); // colIndex is 1-based; colLetter is 0-based
  const idx = rowMeta.findIndex((m) => m.row.key === row.key);
  if (idx < 0) return null;

  // Total Operating Outflows = sum of every fr-sub-* row (+ unmapped positions)
  if (row.key === "fr-total-outflow") {
    const parts = rowMeta
      .filter((m) => m.row.key.startsWith("fr-sub-"))
      .map((m) => `${col}${m.excelRow}`);
    const unmapped = rowMeta
      .filter((m) => m.row.key.startsWith("fr-unmapped-") && !m.row.isHeader)
      .map((m) => `${col}${m.excelRow}`);
    const all = [...parts, ...unmapped];
    return all.length ? all.join("+") : null;
  }

  // Net Operating = Total Sales Inflow - Total Operating Outflows
  if (row.key === "fr-net-operating") {
    const sales = rowMeta.find((m) => m.row.key === "fr-total-sales");
    const outflow = rowMeta.find((m) => m.row.key === "fr-total-outflow");
    if (!sales || !outflow) return null;
    return `${col}${sales.excelRow}-${col}${outflow.excelRow}`;
  }

  // Any other total (fr-total-sales, fr-sub-*) → sum the data rows between the
  // previous header and this row.
  let start = idx - 1;
  while (start >= 0 && !rowMeta[start].row.isHeader) start -= 1;
  const children = rowMeta
    .slice(start + 1, idx)
    .filter((m) => !m.row.isHeader && !m.row.isTotal);
  if (!children.length) return null;
  const first = `${col}${children[0].excelRow}`;
  const last = `${col}${children[children.length - 1].excelRow}`;
  return `SUM(${first}:${last})`;
}
